import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { UiComponent } from 'components';
import { PushQuery } from 'queries';
import { NOTI_MENU_URL } from 'data/menu';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import useModal from 'hooks/useModal';
import { IPushMessage } from 'types';
import {
  PUSH_DESTINATION_LABEL,
  PUSH_STATUS_LABEL,
  PUSH_TYPE_LABEL,
  isEditable,
} from '../constants';
import * as styles from './styles';

interface Props {
  push: IPushMessage;
}

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';

function Detail({ push }: Props) {
  const navigate = useNavigate();
  const { handleConfirm } = useModal();
  const pushUrl = NOTI_MENU_URL['앱 푸시 관리'];
  const { mutate: cancel, isPending } = PushQuery.useCancel();

  const handleCancel = () => {
    handleConfirm({
      confirmContent: '예약된 푸시 발송을 취소하시겠습니까?',
      yesTitle: '예약 취소',
      noTitle: '닫기',
      confirmNext: () =>
        cancel(push.id, {
          onSuccess: () => toast.success('예약이 취소되었습니다.'),
          onError: (error) =>
            toast.error(
              (error as { response?: { data?: { message?: string } } })
                ?.response?.data?.message ?? DEFAULT_ERROR_MESSAGE,
            ),
        }),
    });
  };

  const rows: [string, React.ReactNode][] = [
    ['상태', PUSH_STATUS_LABEL[push.status]],
    ['푸시 유형', PUSH_TYPE_LABEL[push.pushType]],
    ['제목', push.title],
    ['내용', push.body],
    [
      '발송 대상',
      push.targetType === 'ALL'
        ? '전체 회원'
        : `회원 선택 (${push.targetUserIds?.length ?? 0}명)`,
    ],
    [
      '연결 화면',
      `${PUSH_DESTINATION_LABEL[push.destinationType]}${
        push.destinationDetail ? ` (#${push.destinationDetail})` : ''
      }`,
    ],
    ['예약 일시', formatDateTime(push.sendAt)],
    ['실제 발송 일시', formatDateTime(push.sentAt)],
    ['작성자', push.createdBy?.username ?? '-'],
    ['최종 처리자', push.updatedBy?.username ?? '-'],
    ['작성 일시', formatDateTime(push.createdAt)],
    ['수정 일시', formatDateTime(push.updatedAt)],
    ...(push.failReason
      ? ([['실패 사유', push.failReason]] as [string, React.ReactNode][])
      : []),
  ];

  return (
    <>
      <UiComponent.Box>
        <UiComponent.BoxTitle title="기본 정보" />
        <dl css={styles.rows}>
          {rows.map(([label, value]) => (
            <div key={label} css={styles.row}>
              <dt css={styles.label}>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        {isEditable(push.status) && (
          <div css={styles.buttons}>
            <UiComponent.Button
              variant="border"
              color="primary"
              onClick={() => navigate(`${pushUrl}/${push.id}/edit`)}
            >
              수정
            </UiComponent.Button>
            {push.status === 'SCHEDULED' && (
              <UiComponent.Button
                variant="border"
                color="primary"
                onClick={handleCancel}
                disabled={isPending}
              >
                예약 취소
              </UiComponent.Button>
            )}
          </div>
        )}
      </UiComponent.Box>
    </>
  );
}

export default Detail;
